import React, { useRef, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Canvas, useThree } from '@react-three/fiber';
import { Float, Html, Stars, OrbitControls } from '@react-three/drei';
import { Users, Award, Shield, Zap, Globe, ChevronDown, ChevronUp, Target } from 'lucide-react';
import * as THREE from 'three';

// 3D Company Background Elements
const Company3DElements = () => {
  const { scene } = useThree();
  useEffect(() => {
    scene.fog = new THREE.Fog('#0f172a', 1, 15);
  }, [scene]);

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <pointLight position={[-5, -5, -5]} intensity={0.6} color="#6366f1" />
      <pointLight position={[5, -5, -5]} intensity={0.6} color="#f43f5e" />
      <pointLight position={[0, 5, -5]} intensity={0.4} color="#8b5cf6" />
      <Stars radius={100} depth={50} count={3000} factor={4} fade speed={1} />

      {/* Floating Company Icons */}
      <Float speed={1.5} rotationIntensity={0.6} floatIntensity={0.6}>
        <Html position={[-4, 2, 0]} center>
          <div style={{
            background: 'rgba(99, 102, 241, 0.2)',
            borderRadius: '50%',
            padding: '20px',
            border: '2px solid rgba(99, 102, 241, 0.4)',
            backdropFilter: 'blur(15px)',
            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
          }}>
            <Users size={28} color="#6366f1" />
          </div>
        </Html>
      </Float>

      <Float speed={2} rotationIntensity={0.8} floatIntensity={0.8}>
        <Html position={[4, -1, 2]} center>
          <div style={{
            background: 'rgba(244, 63, 94, 0.2)',
            borderRadius: '50%',
            padding: '20px',
            border: '2px solid rgba(244, 63, 94, 0.4)',
            backdropFilter: 'blur(15px)',
            boxShadow: '0 8px 32px rgba(244, 63, 94, 0.3)'
          }}>
            <Award size={28} color="#f43f5e" />
          </div>
        </Html>
      </Float>

      <Float speed={1.8} rotationIntensity={0.7} floatIntensity={0.7}>
        <Html position={[0, 3, -2]} center>
          <div style={{
            background: 'rgba(139, 92, 246, 0.2)',
            borderRadius: '50%',
            padding: '20px',
            border: '2px solid rgba(139, 92, 246, 0.4)',
            backdropFilter: 'blur(15px)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)'
          }}>
            <Shield size={28} color="#8b5cf6" />
          </div>
        </Html>
      </Float>
    </>
  );
};

// Animations
const waveAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(10px); }
`;
const floatAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(180deg); }
  100% { transform: translateY(0px) rotate(360deg); }
`;

// Styled Components
const CompanyContainer = styled.div`
  position: relative;
  width: 100vw;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  color: #fff;
  font-family: 'Roboto', sans-serif;
  overflow: hidden;
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const BackgroundGradient = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(244, 63, 94, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%);
  filter: blur(2rem);
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 80px 40px;

  @media (max-width: 1024px) { padding: 60px 30px; }
  @media (max-width: 768px) { padding: 50px 20px; }
  @media (max-width: 480px) { padding: 40px 15px; }
`;

const Section = styled(motion.section)`margin-bottom: 100px;`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(45deg, #6366f1, #f43f5e, #8b5cf6, #22d3ee);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${waveAnimation} 3s ease-in-out infinite;
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 1024px) { gap: 3rem; }
  @media (max-width: 768px) { grid-template-columns: 1fr; gap: 2rem; }
`;

const AboutContent = styled(motion.div)`padding: 2rem;`;

const AboutTitle = styled.h3`
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const AboutText = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(20px);
  transition: all 0.4s ease;
`;

const StatNumber = styled.div`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  color: #6366f1;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
`;

const WhyChooseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
  backdrop-filter: blur(20px);
  transition: all 0.4s ease;
`;

const FeatureIcon = styled.div`
  width: 80px; height: 80px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1.5rem;
  background: ${props => props.gradient};
  border: 2px solid rgba(255, 255, 255, 0.1);
  animation: ${floatAnimation} 4s ease-in-out infinite;
`;

const FeatureTitle = styled.h4`
  font-size: clamp(1.2rem, 3vw, 1.5rem);
  font-weight: 700;
`;

const FeatureText = styled.p`
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  color: rgba(255, 255, 255, 0.7);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 10px;
`;

const FAQItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  margin-bottom: 1rem;
  backdrop-filter: blur(20px);
`;

const FAQHeader = styled.div`
  padding: 1.5rem 2rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: space-between;
`;

const FAQQuestion = styled.h4`
  font-size: clamp(1rem, 2.5vw, 1.2rem);
  font-weight: 600;
`;

const FAQIcon = styled.div`
  color: #6366f1;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const FAQAnswer = styled(motion.div)`padding: 0 2rem;`;
const FAQText = styled.p`
  font-size: clamp(0.95rem, 2.5vw, 1.1rem);
  padding-bottom: 1.5rem;
`;

const CompanySection = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 1 } },
    hover: { y: -10, scale: 1.05, transition: { duration: 0.4 } }
  };

  const faqData = [
    { question: "What services does Zyntro offer?", answer: "We offer IT solutions including cloud computing, cybersecurity, software development, IT consulting, and managed services." },
    { question: "How experienced is your team?", answer: "Our professionals have 10+ years of experience with certifications in various technologies." },
    { question: "Do you provide 24/7 support?", answer: "Yes, we offer 24/7 technical support and monitoring." },
    { question: "What is your project delivery timeline?", answer: "Small projects: 2-4 weeks, medium: 1-3 months, large: 3-6 months." },
    { question: "Do you offer maintenance services?", answer: "Yes, including updates, patches, monitoring, and support." }
  ];

  const features = [
    { icon: <Users size={32} color="#fff" />, title: "Expert Team", text: "Certified professionals with 10+ years experience.", gradient: "linear-gradient(135deg, #6366f1, #1d4ed8)" },
    { icon: <Award size={32} color="#fff" />, title: "Proven Track Record", text: "500+ projects with 98% satisfaction.", gradient: "linear-gradient(135deg, #f43f5e, #be185d)" },
    { icon: <Shield size={32} color="#fff" />, title: "Security First", text: "Enterprise-grade security protocols.", gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)" },
    { icon: <Zap size={32} color="#fff" />, title: "Fast Delivery", text: "Agile methodology for quick turnaround.", gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)" },
    { icon: <Globe size={32} color="#fff" />, title: "Global Reach", text: "Serving clients worldwide.", gradient: "linear-gradient(135deg, #22d3ee, #0891b2)" },
    { icon: <Target size={32} color="#fff" />, title: "Custom Solutions", text: "Tailored solutions for your needs.", gradient: "linear-gradient(135deg, #10b981, #059669)" }
  ];

  return (
    <CompanyContainer>
      <CanvasContainer>
        <Canvas camera={{ position: [0, 0, 8], fov: 75 }}>
          <Company3DElements />
          <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.3} />
        </Canvas>
      </CanvasContainer>

      <BackgroundGradient />

      <ContentWrapper>
        <Section>
          <SectionTitle>About Us</SectionTitle>
          <AboutGrid>
            <AboutContent>
              <AboutTitle>Transforming Businesses Through Technology</AboutTitle>
              <AboutText>Zyntro Software Solution Pvt Ltd empowers businesses with innovative digital solutions, bridging the gap between technology and success.</AboutText>
              <AboutText>We simplify technology management while maximizing efficiency and security for organizations worldwide.</AboutText>
            </AboutContent>
            <StatsGrid>
              <StatCard><StatNumber>500+</StatNumber><StatLabel>Projects Completed</StatLabel></StatCard>
              <StatCard><StatNumber>98%</StatNumber><StatLabel>Client Satisfaction</StatLabel></StatCard>
              <StatCard><StatNumber>10+</StatNumber><StatLabel>Years Experience</StatLabel></StatCard>
              <StatCard><StatNumber>24/7</StatNumber><StatLabel>Support Available</StatLabel></StatCard>
            </StatsGrid>
          </AboutGrid>
        </Section>

        <Section>
          <SectionTitle>Why Choose Us</SectionTitle>
          <WhyChooseGrid>
            {features.map((f, i) => (
              <FeatureCard key={i} variants={cardVariants} whileHover="hover">
                <FeatureIcon gradient={f.gradient}>{f.icon}</FeatureIcon>
                <FeatureTitle>{f.title}</FeatureTitle>
                <FeatureText>{f.text}</FeatureText>
              </FeatureCard>
            ))}
          </WhyChooseGrid>
        </Section>

        <Section>
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <FAQContainer>
            {faqData.map((faq, i) => (
              <FAQItem key={i}>
                <FAQHeader onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                  <FAQIcon isOpen={openFAQ === i}>
                    {openFAQ === i ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </FAQIcon>
                </FAQHeader>
                {openFAQ === i && (
                  <FAQAnswer initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}>
                    <FAQText>{faq.answer}</FAQText>
                  </FAQAnswer>
                )}
              </FAQItem>
            ))}
          </FAQContainer>
        </Section>
      </ContentWrapper>
    </CompanyContainer>
  );
};

export default CompanySection;
